import { ref } from 'vue'
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore'
import firebase from '../../firebase'

const getPatientStats = () => {
  const gloveStats = ref(new Map())
  const kneeSleeveStats = ref(new Map())
  const pantsStats = ref(new Map())
  const repsStats = ref(new Map())
  const gloveMetrics = ['wristXROM', 'wristYROM', 'wristZROM']
  const kneeMetrics = ['kneeROM', 'calfRange', 'hamstringRange', 'quadRange', 'shinRange']
  const userDeviceTypes = ref(new Set())
  const now = new Date()
  const TimeRange = {
    ONE_WEEK: 'ONE_WEEK',
    ONE_MONTH: 'ONE_MONTH',
    HALF_YEAR: 'HALF_YEAR',
    ONE_YEAR: 'ONE_YEAR'
  }

  // One week ago
  const oneWeekAgo = new Date(now)
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
  const oneWeekAgoTimestamp = Timestamp.fromDate(oneWeekAgo)

  // One month ago
  const oneMonthAgo = new Date(now)
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
  const oneMonthAgoTimestamp = Timestamp.fromDate(oneMonthAgo)

  // Half a year ago (6 months ago)
  const halfYearAgo = new Date(now)
  halfYearAgo.setMonth(halfYearAgo.getMonth() - 6)
  const halfYearAgoTimestamp = Timestamp.fromDate(halfYearAgo)

  // One year ago
  const oneYearAgo = new Date(now)
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
  const oneYearAgoTimestamp = Timestamp.fromDate(oneYearAgo)

  const timeRangeMap = {
    [TimeRange.ONE_WEEK]: oneWeekAgoTimestamp,
    [TimeRange.ONE_MONTH]: oneMonthAgoTimestamp,
    [TimeRange.HALF_YEAR]: halfYearAgoTimestamp,
    [TimeRange.ONE_YEAR]: oneYearAgoTimestamp
  }

  const getPostsessions = async (patientId, timeRange) => {
    try {
      const postsessions = []
      const q = query(
        collection(firebase.db, 'postsessions'),
        where('userId', '==', patientId),
        where('startTime', '>=', timeRangeMap[timeRange]),
        orderBy('startTime', 'desc')
      )
      const querySnapshot = await getDocs(q)

      querySnapshot.forEach((doc) => {
        if (doc.data() && doc.data().postsessions && doc.data().postsessions.length) {
          postsessions.push(doc.data())
        }
      })
      return postsessions
    } catch (error) {
      console.log('Firebase error', error)
    }
  }

  const getOffDeviceSessions = async (patientId, timeRange) => {
    try {
      const offDeviceReps = []
      const q = query(
        collection(firebase.db, 'offDeviceSessions'),
        where('userId', '==', patientId),
        where('date', '>=', timeRangeMap[timeRange]),
        orderBy('date', 'desc')
      )
      const querySnapshot = await getDocs(q)

      querySnapshot.forEach((doc) => {
        if (doc.data() && doc.data().exerciseReps) {
          // Accumulate the sum of all exercise repetitions
          const totalReps = Object.values(doc.data().exerciseReps).reduce((acc, curr) => acc + curr, 0)
          offDeviceReps.push({ reps: totalReps, deviceType: doc.data().deviceType })
        }
      })
      return offDeviceReps
    } catch (error) {
      console.log('Firebase error', error)
    }
  }

  const fetchUserOwnedDeviceTypes = async (userId) => {
    userDeviceTypes.value.clear() // Clear the Set of device types
    const devicesQuery = query(collection(firebase.db, 'devices'), where('userId', '==', userId))
    const querySnapshot = await getDocs(devicesQuery)

    const deviceTypes = new Set() // Initialize an empty Set to store unique device types

    querySnapshot.forEach((doc) => {
      let deviceType = doc.data().deviceType
      if (deviceType === 'Knee Brace') {
        deviceType = 'Knee Sleeve' // Replace 'Knee Brace' with 'Knee Sleeve'
      }
      deviceTypes.add(deviceType)
    })

    // Filter the set to only include 'Glove', 'Knee Sleeve', and 'Pants'
    const allowedTypes = new Set(['Glove', 'Knee Sleeve', 'Pants'])
    const filteredDeviceTypes = new Set([...deviceTypes].filter(type => allowedTypes.has(type)))
    userDeviceTypes.value = filteredDeviceTypes // Update the Set of device types
    return filteredDeviceTypes // Return the filtered Set of device types
  }

  const getStats = async (patientId, timeRange) => {
    const postsessions = await getPostsessions(patientId, timeRange)
    const offDeviceSessions = await getOffDeviceSessions(patientId, timeRange)
    const userDeviceTypes = await fetchUserOwnedDeviceTypes(patientId)

    // Initial clearing of stats
    gloveStats.value.clear()
    kneeSleeveStats.value.clear()
    pantsStats.value.clear()
    repsStats.value.clear()

    // Processing offDeviceSessions for repetitions stats
    if (userDeviceTypes.has('Glove')) {
      repsStats.value.set('Off Device Hand', { total: 0, count: 0, mostRecentValue: null, changePercentage: '0%' })
    }
    if (userDeviceTypes.has('Knee Sleeve')) {
      repsStats.value.set('Off Device Knee', { total: 0, count: 0, mostRecentValue: null, changePercentage: '0%' })
    }
    offDeviceSessions.forEach((reps) => {
      if (reps.deviceType === 'Glove' && userDeviceTypes.has('Glove')) {
        repsStats.value.get('Off Device Hand').total += reps.reps
        repsStats.value.get('Off Device Hand').count++

        if (repsStats.value.get('Off Device Hand').mostRecentValue === null) {
          repsStats.value.get('Off Device Hand').mostRecentValue = reps.reps
        }
      }

      if (reps.deviceType === 'Knee Sleeve' && userDeviceTypes.has('Knee Sleeve')) {
        repsStats.value.get('Off Device Knee').total += reps.reps
        repsStats.value.get('Off Device Knee').count++

        if (repsStats.value.get('Off Device Knee').mostRecentValue === null) {
          repsStats.value.get('Off Device Knee').mostRecentValue = reps.reps
        }
      }
    })

    // Calculate the average and change percentage for reps
    repsStats.value.forEach((value, key) => {
      const average = value.count > 0 ? value.total / value.count : 0
      const changePercentage = value.count > 1 ? ((value.mostRecentValue - average) / average * 100).toFixed(2) + '%' : '0%'
      repsStats.value.set(key, { mostRecentValue: value.mostRecentValue, changePercentage })
    })

    userDeviceTypes.forEach(deviceType => {
      repsStats.value.set(deviceType, { total: 0, count: 0, mostRecentValue: null, changePercentage: '0%' })
    })

    // Initial setup for metrics in each map based on side
    const initializeSideMetrics = (metrics) => {
      return metrics.reduce((acc, metric) => {
        acc[metric] = { total: 0, count: 0, mostRecentValue: null, changePercentage: '0%' }
        return acc
      }, {})
    }

    // Set up for different device stats
    gloveMetrics.forEach(metric => {
      gloveStats.value.set('left', initializeSideMetrics(gloveMetrics))
      gloveStats.value.set('right', initializeSideMetrics(gloveMetrics))
    })

    kneeMetrics.forEach(metric => {
      kneeSleeveStats.value.set('left', initializeSideMetrics(kneeMetrics))
      kneeSleeveStats.value.set('right', initializeSideMetrics(kneeMetrics))
      pantsStats.value.set('left', initializeSideMetrics(kneeMetrics))
      pantsStats.value.set('right', initializeSideMetrics(kneeMetrics))
    })

    // Processing post-sessions
    postsessions.forEach((postsession) => {
      postsession.postsessions.forEach((session) => {
        const deviceType = session.deviceType
        const side = session.side ? session.side.toLowerCase() : 'left' // Normalize side to lowercase
        const metricsToUse = deviceType === 'Glove' ? gloveMetrics : kneeMetrics // Choose metric set based on device type

        const reps = session.repetitions
        if (reps !== undefined && !isNaN(reps) && isFinite(reps)) {
          const repsData = repsStats.value.get(deviceType)
          repsData.total += reps
          repsData.count++
          if (repsData.mostRecentValue === null) {
            repsData.mostRecentValue = reps // Set if first entry
          }
          const repsAverage = repsData.total / repsData.count
          repsData.changePercentage = repsData.count > 1 ? ((repsData.mostRecentValue - repsAverage) / repsAverage * 100).toFixed(2) + '%' : '0%'
          repsStats.value.set(deviceType, repsData) // Update with new data
        }

        metricsToUse.forEach(metric => {
          const value = session[metric]
          if (value !== undefined && !isNaN(value) && isFinite(value)) {
            // Determine the appropriate stats map
            let statsMap
            switch (deviceType) {
              case 'Glove':
                statsMap = gloveStats
                break
              case 'Knee Sleeve':
                statsMap = kneeSleeveStats
                break
              case 'Pants':
                statsMap = pantsStats
                break
            }

            if (!statsMap) return // Skip if no appropriate stats map found

            // Update the metric data for the corresponding side
            const sideMetrics = statsMap.value.get(side) // Get metrics for the specified side
            const metricData = sideMetrics[metric]
            metricData.total += value
            metricData.count++
            if (metricData.mostRecentValue === null) {
              metricData.mostRecentValue = value // Set if first entry
            }
            const metricAverage = metricData.total / metricData.count
            metricData.changePercentage = metricData.count > 1 ? ((metricData.mostRecentValue - metricAverage) / metricAverage * 100).toFixed(2) + '%' : '0%'
            sideMetrics[metric] = metricData // Update with new data
          }
        })
      })
    })

    // Logging for verification
    // console.log('Reps Stats:', repsStats.value)
    // console.log('Glove Stats:', gloveStats.value);
    // console.log('Knee Sleeve Stats:', kneeSleeveStats.value);
    // console.log('Pants Stats:', pantsStats.value);
  }

  return { getStats, TimeRange, gloveStats, kneeSleeveStats, pantsStats, repsStats, userDeviceTypes }
}

export default getPatientStats
